import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, Typography, Button } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import ControlledFileUploadField from '../../../../components/controlledComponents/ControlledFileUploadField.component';
import {
  TFinancialGrant,
  GrantType,
  GrantStatus,
  ApplicationStatus,
  ProviderType,
  DisbursementFrequency,
} from '../../../../../apis/finance/financialGrants.interfaces';
import {
  TDocument,
  TDocumentStatus,
  TDocumentType,
} from '../../../../../apis/documents/documents.interfaces';
import { TTransactionCurrency } from '../../../../../apis/finance/finance.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import {
  useFinancialGrantsState,
  useFinancialGrantsActions,
} from '../../contexts/financialGrants.context';
import { useFinanceService } from '../../../../../apis/finance/useFinanceService';
import dayjs, { Dayjs } from 'dayjs';
import {
  isPickerDateRangeOrdered,
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type FinancialGrantFormData = {
  name: string;
  description: string;
  amount: number;
  currency: TTransactionCurrency;
  disbursementFrequency: DisbursementFrequency;
  grantType: GrantType;
  category: string;
  tags?: string;
  gpaRequirement?: number;
  incomeThreshold?: number;
  documentsRequired?: string;
  additionalRequirements?: string;
  applicationDeadline: Dayjs | null;
  awardDate?: Dayjs | null;
  validityStartDate?: Dayjs | null;
  validityEndDate?: Dayjs | null;
  providerId: string;
  providerName: string;
  providerType: ProviderType;
  recipientId?: string;
  recipientName?: string;
  status: GrantStatus;
  applicationStatus?: ApplicationStatus;
  documents?: Array<{
    _id?: string;
    name: string;
    document_type: string;
    status: string;
    file_key: string;
    title?: string;
    description?: string;
    verificationDate?: string;
  }>;
};

const getFinancialGrantFormSchema = (translateText: (key: string) => string) =>
  z.object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().min(1, translateText('common.fieldRequired')),
    amount: zNumberFromInput({
      min: 0.01,
      message:
        translateText('finance.form.amountMinError') ||
        'Amount must be greater than 0',
    }),
    currency: z.nativeEnum(TTransactionCurrency, {
      required_error: translateText('common.fieldRequired'),
    }),
    disbursementFrequency: z.nativeEnum(DisbursementFrequency, {
      required_error: translateText('common.fieldRequired'),
    }),
    grantType: z.nativeEnum(GrantType, {
      required_error: translateText('common.fieldRequired'),
    }),
    category: z.string().min(1, translateText('common.fieldRequired')),
    tags: z.string().optional(),
    gpaRequirement: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    incomeThreshold: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    documentsRequired: z.string().optional(),
    additionalRequirements: z.string().optional(),
    applicationDeadline: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    awardDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    validityStartDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    validityEndDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    providerId: z.string().min(1, translateText('common.fieldRequired')),
    providerName: z.string().min(1, translateText('common.fieldRequired')),
    providerType: z.nativeEnum(ProviderType, {
      required_error: translateText('common.fieldRequired'),
    }),
    recipientId: z.string().optional(),
    recipientName: z.string().optional(),
    status: z.nativeEnum(GrantStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    applicationStatus: z.nativeEnum(ApplicationStatus).optional(),
    documents: z.array(z.any()).optional(),
  })
    .refine(
      (data) =>
        isPickerDateRangeOrdered(
          data.validityStartDate,
          data.validityEndDate
        ),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['validityEndDate'],
      }
    );

const getCurrencyOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TTransactionCurrency.USD,
      value: translateText('finance.form.currencyUSD') || 'USD',
    },
    {
      key: TTransactionCurrency.EUR,
      value: translateText('finance.form.currencyEUR') || 'EUR',
    },
    {
      key: TTransactionCurrency.GBP,
      value: translateText('finance.form.currencyGBP') || 'GBP',
    },
    {
      key: TTransactionCurrency.INR,
      value: translateText('finance.form.currencyINR') || 'INR',
    },
  ];

const getDisbursementFrequencyOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: DisbursementFrequency.ONE_TIME,
      value:
        translateText('finance.grants.form.disbursementFrequencyOneTime') ||
        'One-time',
    },
    {
      key: DisbursementFrequency.MONTHLY,
      value:
        translateText('finance.grants.form.disbursementFrequencyMonthly') ||
        'Monthly',
    },
    {
      key: DisbursementFrequency.QUARTERLY,
      value:
        translateText('finance.grants.form.disbursementFrequencyQuarterly') ||
        'Quarterly',
    },
    {
      key: DisbursementFrequency.YEARLY,
      value:
        translateText('finance.grants.form.disbursementFrequencyYearly') ||
        'Yearly',
    },
  ];

const getGrantTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: GrantType.SCHOLARSHIP,
      value:
        translateText('finance.grants.form.grantTypeScholarship') ||
        'Scholarship',
    },
    {
      key: GrantType.MERIT_BASED,
      value:
        translateText('finance.grants.form.grantTypeMeritBased') || 'Merit-based',
    },
    {
      key: GrantType.NEED_BASED,
      value:
        translateText('finance.grants.form.grantTypeNeedBased') || 'Need-based',
    },
    {
      key: GrantType.RESEARCH,
      value: translateText('finance.grants.form.grantTypeResearch') || 'Research',
    },
    {
      key: GrantType.ATHLETIC,
      value: translateText('finance.grants.form.grantTypeAthletic') || 'Athletic',
    },
    {
      key: GrantType.MINORITY,
      value: translateText('finance.grants.form.grantTypeMinority') || 'Minority',
    },
    {
      key: GrantType.OTHER,
      value: translateText('finance.grants.form.grantTypeOther') || 'Other',
    },
  ];

const getProviderTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: ProviderType.GOVERNMENT,
      value:
        translateText('finance.grants.form.providerTypeGovernment') ||
        'Government',
    },
    {
      key: ProviderType.PRIVATE,
      value:
        translateText('finance.grants.form.providerTypePrivate') || 'Private',
    },
    {
      key: ProviderType.INSTITUTION,
      value:
        translateText('finance.grants.form.providerTypeInstitution') ||
        'Institution',
    },
    {
      key: ProviderType.FOUNDATION,
      value:
        translateText('finance.grants.form.providerTypeFoundation') ||
        'Foundation',
    },
    {
      key: ProviderType.CORPORATE,
      value:
        translateText('finance.grants.form.providerTypeCorporate') || 'Corporate',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: GrantStatus.OPEN,
      value: translateText('finance.grants.filters.open') || 'Open',
    },
    {
      key: GrantStatus.CLOSED,
      value: translateText('finance.grants.filters.closed') || 'Closed',
    },
    {
      key: GrantStatus.AWARDED,
      value: translateText('finance.grants.filters.awarded') || 'Awarded',
    },
    {
      key: GrantStatus.DISBURSED,
      value: translateText('finance.grants.filters.disbursed') || 'Disbursed',
    },
    {
      key: GrantStatus.EXPIRED,
      value: translateText('finance.grants.filters.expired') || 'Expired',
    },
  ];

const getApplicationStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: ApplicationStatus.PENDING,
      value:
        translateText('finance.grants.form.applicationStatusPending') ||
        'Pending',
    },
    {
      key: ApplicationStatus.UNDER_REVIEW,
      value:
        translateText('finance.grants.form.applicationStatusUnderReview') ||
        'Under Review',
    },
    {
      key: ApplicationStatus.APPROVED,
      value:
        translateText('finance.grants.form.applicationStatusApproved') ||
        'Approved',
    },
    {
      key: ApplicationStatus.REJECTED,
      value:
        translateText('finance.grants.form.applicationStatusRejected') ||
        'Rejected',
    },
  ];

const AddEditFinancialGrantsView: FC = () => {
  const { translateText } = useTranslation();
  const financialGrantFormSchema = getFinancialGrantFormSchema(translateText);
  const { financialGrantsAppState } = useFinancialGrantsState();
  const {
    setIsManageFinancialGrantItemOpen,
    setSelectedFinancialGrantItem,
    setIsCreateMode,
    setIsEditMode,
    setIsFinancialGrantItemLoading,
    setIsFinancialGrantItemError,
    setFinancialGrantItemErrorMessage,
  } = useFinancialGrantsActions();
  const { creatFinanceGrant, patchFinanceGrant } = useFinanceService();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const defaultValues: FinancialGrantFormData = {
    name: '',
    description: '',
    amount: 0,
    currency: TTransactionCurrency.INR,
    disbursementFrequency: DisbursementFrequency.ONE_TIME,
    grantType: GrantType.SCHOLARSHIP,
    category: '',
    tags: '',
    gpaRequirement: undefined,
    incomeThreshold: undefined,
    documentsRequired: '',
    additionalRequirements: '',
    applicationDeadline: dayjs(),
    awardDate: null,
    validityStartDate: null,
    validityEndDate: null,
    providerId: loggedInUserId,
    providerName: userFromStorage
      ? `${userFromStorage.firstName} ${userFromStorage.lastName}`.trim()
      : '',
    providerType: ProviderType.INSTITUTION,
    recipientId: undefined,
    recipientName: undefined,
    status: GrantStatus.OPEN,
    applicationStatus: undefined,
    documents: [],
  };

  const methods = useForm<FinancialGrantFormData>({
    resolver: zodResolver(financialGrantFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (
      financialGrantsAppState.selectedFinancialGrantItem &&
      financialGrantsAppState.isManageFinancialGrantItemOpen
    ) {
      const item = financialGrantsAppState.selectedFinancialGrantItem;
      reset({
        name: item.name || '',
        description: item.description || '',
        amount: item.amount || 0,
        currency: item.currency || TTransactionCurrency.INR,
        disbursementFrequency:
          (item.disbursementFrequency as DisbursementFrequency) ||
          DisbursementFrequency.ONE_TIME,
        grantType: (item.grantType as GrantType) || GrantType.SCHOLARSHIP,
        category: item.category || '',
        tags: item.tags?.join(', ') || '',
        gpaRequirement: item.eligibilityCriteria?.gpaRequirement,
        incomeThreshold: item.eligibilityCriteria?.incomeThreshold,
        documentsRequired:
          item.eligibilityCriteria?.documentsRequired?.join(', ') || '',
        additionalRequirements:
          item.eligibilityCriteria?.additionalRequirements?.join(', ') || '',
        applicationDeadline: item.applicationDeadline
          ? dayjs(item.applicationDeadline)
          : dayjs(),
        awardDate: item.awardDate ? dayjs(item.awardDate) : null,
        validityStartDate: item.validity?.startDate
          ? dayjs(item.validity.startDate)
          : null,
        validityEndDate: item.validity?.endDate
          ? dayjs(item.validity.endDate)
          : null,
        providerId: item.providerId || loggedInUserId,
        providerName: item.providerName || '',
        providerType:
          (item.providerType as ProviderType) || ProviderType.INSTITUTION,
        recipientId: item.recipientId,
        recipientName: item.recipientName,
        status: (item.status as GrantStatus) || GrantStatus.OPEN,
        applicationStatus: item.applicationStatus
          ? (item.applicationStatus as ApplicationStatus)
          : undefined,
        documents:
          item.documents?.map((doc) => ({
            _id: doc.documentId,
            name: doc.documentName,
            document_type: doc.documentType,
            status: doc.status,
            file_key: doc.documentName,
            title: doc.documentName,
            description: doc.description,
            verificationDate: doc.verificationDate,
          })) || [],
      });
    } else if (financialGrantsAppState.isManageFinancialGrantItemOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    financialGrantsAppState.selectedFinancialGrantItem,
    financialGrantsAppState.isManageFinancialGrantItemOpen,
    reset,
  ]);

  const mapToTFinancialGrant = (
    data: FinancialGrantFormData,
    existingItem?: TFinancialGrant
  ): TFinancialGrant => {
    const tagsArray = data.tags
      ? data.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
      : [];

    const documentsRequiredArray = data.documentsRequired
      ? data.documentsRequired
        .split(',')
        .map((doc) => doc.trim())
        .filter((doc) => doc.length > 0)
      : [];

    const additionalRequirementsArray = data.additionalRequirements
      ? data.additionalRequirements
        .split(',')
        .map((req) => req.trim())
        .filter((req) => req.length > 0)
      : [];

    const documentsArray: TDocument[] = data.documents
      ? data.documents.map((doc, index) => {
        // Try to find existing document by documentId or index
        const existingDoc =
          existingItem?.documents?.find(
            (d) => d.documentId === doc._id || d.documentName === doc.file_key
          ) || existingItem?.documents?.[index];

        return {
          documentId: doc._id || existingDoc?.documentId || `doc-${index}`,
          userId: loggedInUserId,
          documentType:
            (doc.document_type as TDocumentType) || TDocumentType.OTHER,
          documentName: doc.file_key || doc.name || '',
          submissionDate: new Date().toISOString().split('T')[0],
          verificationDate: doc.verificationDate,
          status: (doc.status as TDocumentStatus) || TDocumentStatus.PENDING,
          description: doc.description || doc.title,
          fileUrl: existingDoc?.fileUrl,
          thumbnailUrl: existingDoc?.thumbnailUrl,
          uploadedAt: existingDoc?.uploadedAt || {
            userId: loggedInUserId,
            timestamp: Date.now(),
            name: userFromStorage
              ? `${userFromStorage.firstName} ${userFromStorage.lastName}`.trim()
              : undefined,
            email: userFromStorage?.email || undefined,
          },
        };
      })
      : existingItem?.documents || [];

    return {
      id: existingItem?.id || '',
      documents: documentsArray,
      name: data.name,
      description: data.description,
      amount: data.amount,
      currency: data.currency,
      disbursementFrequency: data.disbursementFrequency,
      grantType: data.grantType,
      category: data.category,
      tags: tagsArray,
      eligibilityCriteria: {
        gpaRequirement: data.gpaRequirement,
        incomeThreshold: data.incomeThreshold,
        documentsRequired: documentsRequiredArray,
        additionalRequirements: additionalRequirementsArray,
      },
      applicationDeadline: data.applicationDeadline
        ? data.applicationDeadline.toDate()
        : new Date(),
      awardDate: data.awardDate ? data.awardDate.toDate() : undefined,
      validity: {
        startDate: data.validityStartDate
          ? data.validityStartDate.toDate()
          : new Date(),
        endDate: data.validityEndDate
          ? data.validityEndDate.toDate()
          : undefined,
      },
      providerId: data.providerId,
      providerName: data.providerName,
      providerType: data.providerType,
      recipientId: data.recipientId,
      recipientName: data.recipientName,
      status: data.status as
        | 'open'
        | 'closed'
        | 'awarded'
        | 'disbursed'
        | 'expired',
      applicationStatus: data.applicationStatus as
        | 'pending'
        | 'under-review'
        | 'approved'
        | 'rejected'
        | undefined,
      actions: existingItem?.actions || {
        created: {
          userId: loggedInUserId,
          timestamp: Date.now(),
          name: userFromStorage
            ? `${userFromStorage.firstName} ${userFromStorage.lastName}`.trim()
            : undefined,
          email: userFromStorage?.email || undefined,
        },
      },
    };
  };

  const onSubmitForm = async (data: FinancialGrantFormData) => {
    try {
      setIsFinancialGrantItemLoading(true);
      setIsFinancialGrantItemError(false);
      setFinancialGrantItemErrorMessage('');

      const financialGrantData = mapToTFinancialGrant(
        data,
        financialGrantsAppState.selectedFinancialGrantItem || undefined
      );

      if (financialGrantsAppState.isCreateMode) {
        await creatFinanceGrant(financialGrantData);
      } else if (financialGrantsAppState.isEditMode) {
        await patchFinanceGrant(financialGrantData);
      }

      setIsManageFinancialGrantItemOpen(false);
      setSelectedFinancialGrantItem(null);
      setIsCreateMode(false);
      setIsEditMode(false);
    } catch (error: any) {
      setIsFinancialGrantItemError(true);
      setFinancialGrantItemErrorMessage(
        error?.response?.data?.message ||
        translateText('finance.grants.form.saveError') ||
        'Failed to save financial grant'
      );
    } finally {
      setIsFinancialGrantItemLoading(false);
    }
  };

  const handleClose = () => {
    setIsManageFinancialGrantItemOpen(false);
    setIsFinancialGrantItemLoading(false);
    setIsFinancialGrantItemError(false);
    setFinancialGrantItemErrorMessage('');
  };

  return (
    <div data-testid="add-edit-financial-grant-container">
      <StructuredModal
        isOpen={financialGrantsAppState.isManageFinancialGrantItemOpen}
        onClose={handleClose}
        title={
          financialGrantsAppState.isEditMode
            ? translateText('finance.grants.form.editGrant') ||
            'Edit Financial Grant'
            : translateText('finance.grants.addGrant') || 'Add Financial Grant'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          financialGrantsAppState.isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={financialGrantsAppState.isFinancialGrantItemLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          financialGrantsAppState.financialGrantItemErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="financial-grant-error-message"
            >
              {financialGrantsAppState.financialGrantItemErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="financial-grant-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="financial-grant-form-container"
            >
              <Grid
                item
                xs={12}
                data-testid="financial-grant-basic-details-section"
              >
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.form.basicDetails') ||
                    'Basic Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="name"
                  id="financial-grant-name"
                  label={translateText('finance.grants.columns.name') || 'Name'}
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-amount-field"
              >
                <ControlledTextField
                  control={control}
                  name="amount"
                  id="financial-grant-amount"
                  label={
                    translateText('finance.grants.columns.amount') || 'Amount'
                  }
                  type="number"
                  errorMessage={errors.amount?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-currency-field"
              >
                <ControlledSelectField
                  control={control}
                  name="currency"
                  id="financial-grant-currency"
                  label={translateText('finance.grants.currency') || 'Currency'}
                  itemList={getCurrencyOptions(translateText)}
                  errorMessage={errors.currency?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-disbursement-frequency-field"
              >
                <ControlledSelectField
                  control={control}
                  name="disbursementFrequency"
                  id="financial-grant-disbursement-frequency"
                  label={
                    translateText('finance.grants.disbursementFrequency') ||
                    'Disbursement Frequency'
                  }
                  itemList={getDisbursementFrequencyOptions(translateText)}
                  errorMessage={errors.disbursementFrequency?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-type-field"
              >
                <ControlledSelectField
                  control={control}
                  name="grantType"
                  id="financial-grant-type"
                  label={
                    translateText('finance.grants.columns.type') || 'Grant Type'
                  }
                  itemList={getGrantTypeOptions(translateText)}
                  errorMessage={errors.grantType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-category-field"
              >
                <ControlledTextField
                  control={control}
                  name="category"
                  id="financial-grant-category"
                  label={
                    translateText('finance.grants.columns.category') ||
                    'Category'
                  }
                  errorMessage={errors.category?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-status-field"
              >
                <ControlledSelectField
                  control={control}
                  name="status"
                  id="financial-grant-status"
                  label={
                    translateText('finance.grants.columns.status') || 'Status'
                  }
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-application-status-field"
              >
                <ControlledSelectField
                  control={control}
                  name="applicationStatus"
                  id="financial-grant-application-status"
                  label={
                    translateText('finance.grants.form.applicationStatus') ||
                    'Application Status'
                  }
                  itemList={getApplicationStatusOptions(translateText)}
                  errorMessage={errors.applicationStatus?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                data-testid="financial-grant-description-field"
              >
                <ControlledTextField
                  control={control}
                  name="description"
                  id="financial-grant-description"
                  label={
                    translateText('finance.grants.description') || 'Description'
                  }
                  errorMessage={errors.description?.message}
                  required
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} data-testid="financial-grant-tags-field">
                <ControlledTextField
                  control={control}
                  name="tags"
                  id="financial-grant-tags"
                  label={translateText('finance.form.tags') || 'Tags'}
                  helperText={
                    translateText('finance.form.tagsHelper') ||
                    'Separate tags with commas'
                  }
                  errorMessage={errors.tags?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                data-testid="financial-grant-eligibility-section"
              >
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.grants.form.eligibilityCriteria') ||
                    'Eligibility Criteria'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-gpa-requirement-field"
              >
                <ControlledTextField
                  control={control}
                  name="gpaRequirement"
                  id="financial-grant-gpa-requirement"
                  label={
                    translateText('finance.grants.form.gpaRequirement') ||
                    'GPA Requirement'
                  }
                  type="number"
                  errorMessage={errors.gpaRequirement?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-income-threshold-field"
              >
                <ControlledTextField
                  control={control}
                  name="incomeThreshold"
                  id="financial-grant-income-threshold"
                  label={
                    translateText('finance.grants.form.incomeThreshold') ||
                    'Income Threshold'
                  }
                  type="number"
                  errorMessage={errors.incomeThreshold?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-documents-required-field"
              >
                <ControlledTextField
                  control={control}
                  name="documentsRequired"
                  id="financial-grant-documents-required"
                  label={
                    translateText('finance.grants.form.documentsRequired') ||
                    'Documents Required'
                  }
                  helperText={
                    translateText('finance.form.tagsHelper') ||
                    'Separate documents with commas'
                  }
                  errorMessage={errors.documentsRequired?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-additional-requirements-field"
              >
                <ControlledTextField
                  control={control}
                  name="additionalRequirements"
                  id="financial-grant-additional-requirements"
                  label={
                    translateText(
                      'finance.grants.form.additionalRequirements'
                    ) || 'Additional Requirements'
                  }
                  helperText={
                    translateText('finance.form.tagsHelper') ||
                    'Separate requirements with commas'
                  }
                  errorMessage={errors.additionalRequirements?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} data-testid="financial-grant-dates-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.grants.form.dates') || 'Dates'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-application-deadline-field"
              >
                <ControlledDateField
                  control={control}
                  name="applicationDeadline"
                  id="financial-grant-application-deadline"
                  label={
                    translateText('finance.grants.applicationDeadline') ||
                    'Application Deadline'
                  }
                  errorMessage={errors.applicationDeadline?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-award-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="awardDate"
                  id="financial-grant-award-date"
                  label={
                    translateText('finance.grants.form.awardDate') ||
                    'Award Date'
                  }
                  errorMessage={errors.awardDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-validity-start-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="validityStartDate"
                  id="financial-grant-validity-start-date"
                  label={
                    translateText('finance.grants.form.validityStartDate') ||
                    'Validity Start Date'
                  }
                  errorMessage={errors.validityStartDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-validity-end-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="validityEndDate"
                  id="financial-grant-validity-end-date"
                  label={
                    translateText('finance.grants.form.validityEndDate') ||
                    'Validity End Date'
                  }
                  errorMessage={errors.validityEndDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="financial-grant-provider-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.grants.form.providerDetails') ||
                    'Provider Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-provider-id-field"
              >
                <ControlledTextField
                  control={control}
                  name="providerId"
                  id="financial-grant-provider-id"
                  label={
                    translateText('finance.grants.form.providerId') ||
                    'Provider ID'
                  }
                  errorMessage={errors.providerId?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-provider-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="providerName"
                  id="financial-grant-provider-name"
                  label={
                    translateText('finance.grants.provider') || 'Provider Name'
                  }
                  errorMessage={errors.providerName?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-provider-type-field"
              >
                <ControlledSelectField
                  control={control}
                  name="providerType"
                  id="financial-grant-provider-type"
                  label={
                    translateText('finance.grants.form.providerType') ||
                    'Provider Type'
                  }
                  itemList={getProviderTypeOptions(translateText)}
                  errorMessage={errors.providerType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                data-testid="financial-grant-recipient-section"
              >
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.grants.form.recipientDetails') ||
                    'Recipient Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-recipient-id-field"
              >
                <ControlledTextField
                  control={control}
                  name="recipientId"
                  id="financial-grant-recipient-id"
                  label={
                    translateText('finance.grants.form.recipientId') ||
                    'Recipient ID'
                  }
                  errorMessage={errors.recipientId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="financial-grant-recipient-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="recipientName"
                  id="financial-grant-recipient-name"
                  label={
                    translateText('finance.grants.form.recipientName') ||
                    'Recipient Name'
                  }
                  errorMessage={errors.recipientName?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                data-testid="financial-grant-documents-section"
              >
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.list.documents') || 'Documents'}
                </Typography>
              </Grid>

              {(methods.watch('documents') || []).map(
                (_doc: any, index: number) => (
                  <Grid
                    item
                    xs={12}
                    key={index}
                    data-testid={`financial-grant-document-${index}`}
                  >
                    <ControlledFileUploadField
                      control={control}
                      name={`documents.${index}.file_key`}
                      id={`financial-grant-document-${index}`}
                      label={
                        translateText('documents.documentFile') ||
                        'Document File'
                      }
                      sectionTitle={`${translateText('documents.document') || 'Document'} ${index + 1}`}
                      titleName={`documents.${index}.name`}
                      descriptionName={`documents.${index}.description`}
                      statusName={`documents.${index}.status`}
                      verificationName={`documents.${index}.verificationDate`}
                    />
                  </Grid>
                )
              )}

              <Grid
                item
                xs={12}
                data-testid="financial-grant-add-document-button"
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    const currentDocs = methods.getValues('documents') || [];
                    methods.setValue('documents', [
                      ...currentDocs,
                      {
                        name: '',
                        document_type: '',
                        status: 'pending',
                        file_key: '',
                      },
                    ]);
                  }}
                  data-testid="add-document-button"
                >
                  {translateText('documents.addDocument') || 'Add Document'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditFinancialGrantsView;
